import React, { useId } from 'react';

/**
 * Checkbox
 * ------------------------------------------------------------------
 * Accessible custom checkbox. Controlled via `checked` / `onChange`.
 * Uses a button with role="checkbox" so clicks are reliable (avoids
 * clipped native inputs that fail to toggle in some layouts).
 *
 * @param {boolean} checked
 * @param {function} onChange - receives an event-like object with target.checked
 * @param {string} label
 * @param {string} checkIconSrc - path to the checkmark icon image
 */
const CustomCheckbox = ({
  checked = false,
  onChange,
  label,
  checkIconSrc,
  id,
  className = '',
  disabled = false,
  ...rest
}) => {
  const autoId = useId();
  const checkboxId = id || autoId;

  const handleToggle = (event) => {
    if (disabled) return;
    event.preventDefault();
    const nextChecked = !checked;
    onChange?.({
      ...event,
      target: { ...event.target, checked: nextChecked },
      currentTarget: { ...event.currentTarget, checked: nextChecked },
    });
  };

  return (
    <button
      type="button"
      id={checkboxId}
      role="checkbox"
      aria-checked={checked}
      aria-label={label || undefined}
      disabled={disabled}
      className={`amok-checkbox ${checked ? 'amok-checkbox--checked' : ''} ${className}`}
      onClick={handleToggle}
      {...rest}
    >
      {checked && checkIconSrc ? (
        <img className="amok-checkbox__check" src={checkIconSrc} alt="" />
      ) : (
        <span className="amok-checkbox__box" aria-hidden="true" />
      )}
      {label && <span className="amok-checkbox__label">{label}</span>}
    </button>
  );
};

export default CustomCheckbox;
